/**
 * API client for backend communication.
 * Token is stored in sessionStorage for session-only persistence.
 */

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function getToken() {
  return sessionStorage.getItem('auth_token');
}

export { getToken };

function setToken(token) {
  sessionStorage.setItem('auth_token', token);
}

function removeToken() {
  sessionStorage.removeItem('auth_token');
  sessionStorage.removeItem('csrf_token');
}

function getCsrfToken() {
  return sessionStorage.getItem('csrf_token');
}

function setCsrfToken(token) {
  sessionStorage.setItem('csrf_token', token);
}

async function fetchCsrfToken() {
  const token = getToken();
  if (!token) return;
  try {
    const res = await fetch(`${API_BASE}/auth/csrf.php`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (res.ok) {
      const data = await res.json();
      if (data.csrf_token) setCsrfToken(data.csrf_token);
    }
  } catch {
    // Best effort
  }
}

async function request(endpoint, options = {}) {
  const token = getToken();
  const headers = { ...(options.headers || {}) };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Add CSRF token for state-changing requests
  const method = (options.method || 'GET').toUpperCase();
  if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
    const csrf = getCsrfToken();
    if (csrf) {
      headers['X-CSRF-Token'] = csrf;
    }
  }

  // Don't set Content-Type for FormData (browser sets boundary automatically)
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  // Handle 401 - token expired or invalid
  if (response.status === 401) {
    removeToken();
    window.location.href = '/login';
    return null;
  }

  // For file downloads
  const contentType = response.headers.get('Content-Type') || '';
  if (contentType.includes('spreadsheetml') || contentType.includes('octet-stream')) {
    if (!response.ok) throw new Error('Error al descargar archivo');
    return response.blob();
  }

  const text = await response.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    if (response.status === 504) {
      throw new Error('La importacion ha tardado demasiado y el servidor la ha cancelado (504).');
    }
    throw new Error(`Error del servidor (${response.status}). La respuesta no es JSON valido.`);
  }

  if (!response.ok) {
    const detail = data?.detail ? ` (${data.detail})` : '';
    throw new Error(`${data?.error || `Error ${response.status}`}${detail}`);
  }

  return data;
}

// Auth endpoints
export async function login(identifier, password) {
  const data = await request('/auth/login.php', {
    method: 'POST',
    body: JSON.stringify({ identifier, password }),
  });
  if (data && data.token) {
    setToken(data.token);
    await fetchCsrfToken();
  }
  return data;
}

export async function logout() {
  try {
    await request('/auth/logout.php', { method: 'POST' });
  } finally {
    removeToken();
  }
}

export async function getMe() {
  return request('/auth/me.php');
}

export async function changePassword(currentPassword, newPassword) {
  return request('/auth/change_password.php', {
    method: 'POST',
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });
}
export async function changePasswordFromLogin(identifier, currentPassword, newPassword) {
  return request('/auth/change_password_public.php', {
    method: 'POST',
    body: JSON.stringify({
      identifier,
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });
}
// Analytics endpoints
export async function trackEvents(events) {
  return request('/analytics/track.php', {
    method: 'POST',
    body: JSON.stringify({ events }),
  });
}

export async function getDashboardMetric(metric, params = {}) {
  const qs = new URLSearchParams({ metric, ...params }).toString();
  return request(`/analytics/dashboard.php?${qs}`);
}

export async function exportDashboard(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return request(`/analytics/export_dashboard.php?${qs}`);
}

export async function resetDashboard() {
  return request('/analytics/reset_dashboard.php', { method: 'DELETE' });
}

export async function resetUserAnalytics(userId) {
  return request(`/analytics/reset_dashboard.php?user_id=${userId}`, { method: 'DELETE' });
}

export async function getFeatureFlags() {
  return request('/admin/feature_flags.php');
}

export async function updateFeatureFlag(key, enabled) {
  return request('/admin/feature_flags.php', {
    method: 'PUT',
    body: JSON.stringify({ key, enabled }),
  });
}

export async function updateTabOrder(orderedKeys) {
  return request('/admin/feature_flags.php', {
    method: 'PATCH',
    body: JSON.stringify({ tab_order: orderedKeys }),
  });
}

// Admin endpoints
export async function getUsers(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return request(`/admin/users.php?${qs}`);
}

export async function createUser(userData) {
  return request('/admin/users.php', {
    method: 'POST',
    body: JSON.stringify(userData),
  });
}

export async function updateUser(id, userData) {
  return request(`/admin/users.php?id=${id}`, {
    method: 'PUT',
    body: JSON.stringify(userData),
  });
}

export async function deactivateUser(id) {
  return request(`/admin/users.php?id=${id}`, { method: 'DELETE' });
}

export async function reactivateUser(id) {
  return request(`/admin/users.php?id=${id}`, { method: 'PATCH' });
}

export async function purgeUser(id) {
  return request(`/admin/users.php?id=${id}&purge=1`, { method: 'DELETE' });
}

export async function bulkDeleteUsers(ids, purge = false) {
  return request('/admin/users.php', {
    method: 'DELETE',
    body: JSON.stringify({ ids, purge }),
  });
}

export async function importUsers(file, options = {}) {
  const { onUploadProgress, onProcessProgress } = options;
  const formData = new FormData();
  formData.append('action', 'start');
  formData.append('file', file);

  const start = await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE}/admin/import_users_chunked.php`);

    const token = getToken();
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    const csrf = getCsrfToken();
    if (csrf) {
      xhr.setRequestHeader('X-CSRF-Token', csrf);
    }

    xhr.upload.onprogress = (event) => {
      if (!onUploadProgress || !event.lengthComputable) return;
      const percent = Math.min(100, Math.round((event.loaded / event.total) * 100));
      onUploadProgress({
        loaded: event.loaded,
        total: event.total,
        percent,
      });
    };

    xhr.upload.onload = () => {
      if (onUploadProgress) {
        onUploadProgress({
          loaded: file.size,
          total: file.size,
          percent: 100,
        });
      }
    };

    xhr.onload = () => {
      if (xhr.status === 401) {
        removeToken();
        window.location.href = '/login';
        resolve(null);
        return;
      }

      let data = null;
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        if (xhr.status === 504) {
          reject(new Error('La importacion ha tardado demasiado y el servidor la ha cancelado (504). Prueba con un archivo mas pequeno o vuelve a intentarlo.'));
          return;
        }

        if (xhr.status >= 400) {
          reject(new Error(`Error del servidor (${xhr.status}). La respuesta no es JSON valido.`));
          return;
        }

        reject(new Error('Respuesta no valida del servidor'));
        return;
      }

      if (xhr.status < 200 || xhr.status >= 300) {
        const detail = data?.detail ? ` (${data.detail})` : '';
        reject(new Error(`${data?.error || `Error ${xhr.status}`}${detail}`));
        return;
      }

      resolve(data);
    };

    xhr.onerror = () => reject(new Error('Error de red al preparar la importacion'));
    xhr.onabort = () => reject(new Error('Importacion cancelada'));
    xhr.send(formData);
  });

  const totalRows = Number(start?.total_rows ?? 0);
  let processedRows = Number(start?.processed_rows ?? 0);
  let latestResult = start?.results ?? null;

  if (onProcessProgress) {
    onProcessProgress({
      processedRows,
      totalRows,
      percent: totalRows > 0 ? Math.round((processedRows / totalRows) * 100) : 100,
    });
  }

  while (!start?.done && processedRows < totalRows) {
    const data = await request('/admin/import_users_chunked.php', {
      method: 'POST',
      body: JSON.stringify({
        action: 'process',
        job_id: start.job_id,
        batch_size: 25,
      }),
    });

    processedRows = Number(data?.processed_rows ?? processedRows);
    latestResult = data?.results ?? latestResult;

    if (onProcessProgress) {
      onProcessProgress({
        processedRows,
        totalRows: Number(data?.total_rows ?? totalRows),
        percent: totalRows > 0 ? Math.min(100, Math.round((processedRows / totalRows) * 100)) : 100,
      });
    }

    if (data?.done) {
      return data.results;
    }
  }

  return latestResult || {};
}

export async function exportUsers(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return request(`/admin/export_users.php?${qs}`);
}

export async function downloadTemplate() {
  return request('/admin/download_template.php');
}

export async function getConcesionarios(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return request(`/admin/concesionarios.php?${qs}`);
}

export async function createConcesionario(data) {
  return request('/admin/concesionarios.php', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateConcesionario(id, data) {
  return request(`/admin/concesionarios.php?id=${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteConcesionario(id) {
  return request(`/admin/concesionarios.php?id=${id}`, { method: 'DELETE' });
}

export async function bulkDeleteConcesionarios(ids) {
  return request('/admin/concesionarios.php', {
    method: 'DELETE',
    body: JSON.stringify({ ids }),
  });
}

export async function importConcesionarios(file) {
  const formData = new FormData();
  formData.append('file', file);
  return request('/admin/import_concesionarios.php', {
    method: 'POST',
    body: formData,
  });
}

export async function exportConcesionarios() {
  return request('/admin/export_concesionarios.php');
}

export async function getInstalaciones(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return request(`/admin/instalaciones.php?${qs}`);
}

export async function createInstalacion(data) {
  return request('/admin/instalaciones.php', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateInstalacion(id, data) {
  return request(`/admin/instalaciones.php?id=${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteInstalacion(id) {
  return request(`/admin/instalaciones.php?id=${id}`, { method: 'DELETE' });
}

export async function bulkDeleteInstalaciones(ids) {
  return request('/admin/instalaciones.php', {
    method: 'DELETE',
    body: JSON.stringify({ ids }),
  });
}

// GDPR
export async function exportUserData() {
  return request('/analytics/export_user_data.php');
}

export async function deleteUserData() {
  return request('/analytics/delete_user_data.php', { method: 'DELETE' });
}

export async function downloadTemplateConcesionarios() {
  return request('/admin/download_template_concesionarios.php');
}

export async function downloadTemplateInstalaciones() {
  return request('/admin/download_template_instalaciones.php');
}

export async function importInstalaciones(file) {
  const formData = new FormData();
  formData.append('file', file);
  return request('/admin/import_instalaciones.php', { method: 'POST', body: formData });
}

export async function exportInstalaciones() {
  return request('/admin/export_instalaciones.php');
}

// JV Assignments
export async function getJVList() {
  return request('/admin/jv_assignments.php');
}

export async function getJVComerciales(jvId) {
  return request(`/admin/jv_assignments.php?jv_id=${jvId}`);
}

export async function getComercialesByConcesionario(concesionarioId) {
  return request(`/admin/jv_assignments.php?concesionario_id=${concesionarioId}`);
}

export async function assignComercialesToJV(jvId, comercialIds) {
  return request('/admin/jv_assignments.php', {
    method: 'POST',
    body: JSON.stringify({ jv_id: jvId, comercial_ids: comercialIds }),
  });
}

export { removeToken, fetchCsrfToken };


